import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError } from '../../lib/api';
import { useSession } from '../../stores/session';
import { Artwork } from '../dashboard/Artwork';
import { playlists } from '../library/api';
import { duration, rooms } from './api';

/**
 * Filling the queue from one of the host's playlists.
 *
 * ## Why the tracks go into the queue rather than replacing it
 *
 * The room has one source - `station_queue` - and this puts tracks into it.
 * That keeps every control a host already has: reorder, remove, pin, skip. The
 * alternative was a second source the deck could switch to, which sounds
 * tidier and takes all of that away: you cannot reorder a playlist mid-set from
 * the console, and dropping one track means editing the playlist itself.
 *
 * ## Play next, not Add, for a warm-up
 *
 * A host pick sits above every weight and cannot be out-bid - see `Weighting`.
 * So "Play next" is what makes a playlist a warm-up set that submissions cannot
 * push in front of, while "Add" drops a track into the ordinary sort. Both
 * intentions exist here for the same reason they exist in the crate: they are
 * different, and a host mid-broadcast should not have to guess which one a
 * single button meant.
 *
 * `Add all` pins, because somebody adding twenty tracks at once is opening a
 * set rather than making twenty individual suggestions to their own queue.
 */
export function PlaylistsPanel({ onQueued }: { onQueued: () => void }) {
  const me = useSession((state) => state.user);
  const [openId, setOpenId] = useState<number | null>(null);
  const [added, setAdded] = useState<Record<number, 'queued' | 'pinned'>>({});
  const [failed, setFailed] = useState<string | null>(null);

  const { data: lists, isPending } = useQuery({
    queryKey: ['playlists', me?.username],
    queryFn: () => playlists.mine(me!.username),
    enabled: Boolean(me?.username),
  });

  const { data: open } = useQuery({
    queryKey: ['playlist', openId],
    queryFn: () => playlists.get(openId!),
    enabled: openId !== null,
  });

  const add = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: boolean }) => rooms.add(id, pin),
    onSuccess: (_result, { id, pin }) => {
      setAdded((current) => ({ ...current, [id]: pin ? 'pinned' : 'queued' }));
      onQueued();
    },
    onError: (error) => {
      setFailed(error instanceof ApiError ? error.message : 'That could not be added.');
    },
  });

  /**
   * Everything in the playlist, in order, pinned.
   *
   * Sequential rather than `Promise.all`: the queue's order is the order these
   * arrive in, and firing twenty requests at once puts them in whatever order
   * the server finishes them - which for a set list is the one thing that must
   * not be left to chance. It also keeps a rate limit happy.
   */
  const addAll = useMutation({
    mutationFn: async (trackIds: number[]) => {
      for (const id of trackIds) {
        await rooms.add(id, true);
      }
    },
    onSuccess: () => {
      setAdded((current) => ({
        ...current,
        ...Object.fromEntries((open?.tracks ?? []).map((t) => [t.id, 'pinned' as const])),
      }));
      onQueued();
    },
    onError: (error) => {
      setFailed(error instanceof ApiError ? error.message : 'Some of those could not be added.');
    },
  });

  if (isPending) {
    return (
      <div className="mt-4 flex flex-col gap-2" aria-busy="true">
        {[0, 1].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const items = lists?.items ?? [];

  if (items.length === 0) {
    return (
      <Card className="mt-4 p-6 text-sm text-soft">
        No playlists yet. Build one from the library and it will be here to open a set with.
      </Card>
    );
  }

  const playable = (open?.tracks ?? []).filter((track) => track.status === 'ready');

  return (
    <div className="mt-4 flex flex-col gap-4" data-playlists-panel>
      <div className="flex flex-wrap gap-2">
        {items.map((list) => (
          <button
            key={list.id}
            type="button"
            data-console-playlist={list.id}
            aria-pressed={openId === list.id}
            onClick={() => setOpenId(openId === list.id ? null : list.id)}
            className={`rounded-[var(--radius-pill)] border px-3 py-1.5 text-[13px] transition-colors ${
              openId === list.id
                ? 'border-accent bg-accent text-accent-ink'
                : 'border-line text-soft hover:border-line-strong hover:text-ink'
            }`}
          >
            {list.title}
            <span className="ml-2 font-mono text-[11px] opacity-70">{list.track_count}</span>
          </button>
        ))}
      </div>

      {failed && <p className="text-[13px] text-danger">{failed}</p>}

      {openId !== null && (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] text-faint">
              {playable.length === 1 ? '1 track' : `${playable.length} tracks`} ready
            </span>

            <Button
              variant="secondary"
              disabled={playable.length === 0}
              loading={addAll.isPending}
              data-playlist-add-all
              onClick={() => addAll.mutate(playable.map((track) => track.id))}
            >
              Add all as picks
            </Button>
          </div>

          <ul className="flex flex-col gap-1">
            {playable.map((track) => (
              <li
                key={track.id}
                data-console-playlist-track={track.id}
                className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line p-2"
              >
                <Artwork track={track} className="h-9 w-9 shrink-0 rounded-[var(--radius-chip)]" />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{track.title}</span>
                  <span className="block truncate font-mono text-[11px] text-faint">
                    {track.owner?.display_name ?? track.owner?.username ?? ''}
                  </span>
                </span>

                <span className="shrink-0 font-mono text-[11px] text-faint">
                  {duration(track.duration_ms)}
                </span>

                {added[track.id] ? (
                  <span className="meta-caps shrink-0 text-fresh">{added[track.id]}</span>
                ) : (
                  <span className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-[12px]"
                      disabled={add.isPending}
                      data-playlist-track-add={track.id}
                      onClick={() => add.mutate({ id: track.id, pin: false })}
                    >
                      Add
                    </Button>
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-[12px]"
                      disabled={add.isPending}
                      data-playlist-track-pin={track.id}
                      onClick={() => add.mutate({ id: track.id, pin: true })}
                    >
                      Play next
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
